import { PAYABLE_STAGES } from './schemas';
import { centsToInput, textToCents } from './money';
/** Absent on referrals made before verification existed (and on older backends): verified. */
export function verificationOf(r) {
    return r.verification === 'unverified' || r.verification === 'rejected' ? r.verification : 'verified';
}
export function verificationTag(v) {
    if (v === 'unverified')
        return { label: 'Unverified', tone: 'amber' };
    if (v === 'rejected')
        return { label: 'Rejected', tone: 'red' };
    return null;
}
const MONEY_STAGES = [...PAYABLE_STAGES, 'under_contract', 'settled'];
/**
 * Why a move is not offered, or null when it is: a rejected referral never
 * moves; an unverified one moves only where no fee is quoted or paid; a
 * duplicate under desk_decides waits for the desk (it may still lapse).
 */
export function moveBlockedReason(r, to, duplicateMode) {
    const v = verificationOf(r);
    if (v === 'rejected')
        return 'This referral was rejected.';
    if (r.duplicateOf && duplicateMode === 'desk_decides' && to !== 'lapsed')
        return 'Resolve the duplicate first.';
    if (v === 'unverified' && MONEY_STAGES.includes(to))
        return 'Verify the referrer first. Fees are not quoted while a referral is unverified.';
    return null;
}
/** Stage names typed one per line (or comma separated): trimmed, de-duplicated (case-insensitive), at most 50. */
export function parseStageList(text) {
    const seen = new Set();
    const out = [];
    for (const raw of text.split(/[\n,]/)) {
        const s = raw.trim().slice(0, 120);
        if (!s || seen.has(s.toLowerCase()))
            continue;
        seen.add(s.toLowerCase());
        out.push(s);
        if (out.length === 50)
            break;
    }
    return out;
}
export function draftFromSettings(s) {
    return {
        mode: ['first_wins', 'desk_decides', 'split'].includes(s.duplicateRule.mode) ? s.duplicateRule.mode : 'first_wins',
        splitPercent: s.duplicateRule.splitPercent !== undefined ? String(s.duplicateRule.splitPercent) : '50',
        crmEnabled: s.crmStageMap !== null && s.crmStageMap !== undefined,
        crmProvider: s.crmStageMap?.provider ?? 'mycrm',
        contacted: (s.crmStageMap?.contacted ?? []).join('\n'),
        settled: (s.crmStageMap?.settled ?? []).join('\n'),
        rewardEnabled: s.clientReward?.enabled ?? false,
        rewardAmount: s.clientReward ? centsToInput(s.clientReward.amountCents) : '',
    };
}
/**
 * The update to send, or the field errors. Money becomes integer cents here and nowhere else.
 * `includeCrmStageMap: false` (the partner portal, which has no CRM) leaves the stage map out of
 * the update entirely, so a portal save can never write or clear it.
 */
export function settingsFromDraft(d, options = {}) {
    const includeCrm = options.includeCrmStageMap ?? true;
    const errors = {};
    let duplicateRule = { mode: d.mode };
    if (d.mode === 'split') {
        const n = Number(d.splitPercent.trim());
        if (!/^\d{1,2}$/.test(d.splitPercent.trim()) || n < 1 || n > 99)
            errors.splitPercent = 'Enter a whole percentage from 1 to 99.';
        else
            duplicateRule = { mode: 'split', splitPercent: n };
    }
    let crmStageMap = null;
    if (includeCrm && d.crmEnabled) {
        const contacted = parseStageList(d.contacted);
        const settled = parseStageList(d.settled);
        if (contacted.length + settled.length === 0)
            errors.contacted = 'Enter at least one CRM stage name.';
        crmStageMap = { provider: d.crmProvider.trim() || 'mycrm', contacted, settled };
    }
    let clientReward = null;
    if (d.rewardEnabled || d.rewardAmount.trim()) {
        const cents = textToCents(d.rewardAmount);
        if (cents === null || cents <= 0) {
            if (d.rewardEnabled)
                errors.rewardAmount = 'Enter the reward amount in dollars.';
        }
        else {
            clientReward = { enabled: d.rewardEnabled, amountCents: cents, trigger: 'settled' };
        }
    }
    if (Object.keys(errors).length > 0)
        return { ok: false, errors };
    return { ok: true, value: includeCrm ? { duplicateRule, crmStageMap, clientReward } : { duplicateRule, clientReward } };
}
export const DUPLICATE_RULE_COPY = {
    first_wins: {
        label: 'First referral wins',
        body: 'A second referral for a client you already have is refused. This is how the network has always worked.',
    },
    desk_decides: {
        label: 'The desk decides',
        body: 'A second referral is kept and marked as a duplicate. It cannot move until you keep it or the original.',
    },
    split: {
        label: 'Split the fee',
        body: 'Both referrals are kept. Every paid stage, from the strategy call to settlement, is paid once for the client and shared between the two referrers.',
    },
};
